import React, { useState, useEffect, useCallback } from "react";
import { Card, Typography, CircularProgress, ButtonGroup, Button, LinearProgress, Paper } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { Satellite, ZoomIn, ZoomOut, MyLocation, Layers, Refresh } from "@material-ui/icons";
import { withScriptjs, withGoogleMap, GoogleMap, Marker, OverlayView } from "react-google-maps";
import apiService from "../../../services/api";

const useStyles = makeStyles(theme => ({
  root: {
    height: "100%",
    position: "relative",
    display: "flex",
    flexDirection: "column"
  },
  mapContainer: {
    height: "100%",
    borderRadius: theme.shape.borderRadius,
    flex: 1
  },
  controlPanel: {
    position: "absolute",
    top: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 1,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1)
  },
  controlButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark
    }
  },
  title: {
    position: "absolute",
    top: theme.spacing(2),
    left: theme.spacing(2),
    zIndex: 1,
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius
  },
  visualizationControls: {
    position: "absolute",
    top: theme.spacing(2),
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1,
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1]
  },
  error: {
    position: "absolute",
    bottom: theme.spacing(2),
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1,
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1]
  }
}));

const MapComponent = withScriptjs(withGoogleMap((props) => {
  const { center, satelliteImage, isLoading, visualizationType } = props;
  
  return (
    <GoogleMap
      defaultZoom={12}
      center={center || { lat: -1.2921, lng: 36.8219 }}
      mapTypeId={satelliteImage ? "roadmap" : "satellite"} // Use roadmap when we have our own satellite imagery
      options={{
        mapTypeControl: false,
        fullscreenControl: false,
      }}
    >
      {/* Farm location marker */}
      {center && <Marker position={center} />}
      
      {/* Custom satellite imagery overlay */}
      {satelliteImage && center && (
        <OverlayView
          position={center}
          mapPaneName={OverlayView.OVERLAY_LAYER}
          getPixelPositionOffset={(width, height) => ({
            x: -(width / 2),
            y: -(height / 2)
          })}
        >
          <div style={{
            position: 'relative',
            width: '500px',
            height: '500px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <img 
              src={`data:image/png;base64,${satelliteImage}`}
              alt={`${visualizationType} satellite view`}
              style={{
                width: '100%',
                height: '100%',
                opacity: 0.8,
                borderRadius: '4px',
                border: '2px solid #fff',
                boxShadow: '0 2px 10px rgba(0,0,0,0.3)'
              }}
            />
            {isLoading && (
              <CircularProgress 
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginLeft: '-20px',
                  marginTop: '-20px'
                }}
              />
            )}
          </div>
        </OverlayView>
      )}
    </GoogleMap>
  );
}));

export default function MapView({ farmId, farmLocation }) {
  const classes = useStyles();
  const [satelliteImage, setSatelliteImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [visualizationType, setVisualizationType] = useState('true-color');
  const [error, setError] = useState(null);
  const [processingTask, setProcessingTask] = useState(null);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingMessage, setProcessingMessage] = useState('');
  const [showDebug, setShowDebug] = useState(false);
  
  // Force debug to be visible when NDVI is selected
  useEffect(() => {
    if (visualizationType === 'ndvi') {
      setShowDebug(true);
    }
  }, [visualizationType]);
  
  // Center coordinates for the map (default or from farm data)
  const center = farmLocation && farmLocation.lat && farmLocation.lng
    ? { lat: farmLocation.lat, lng: farmLocation.lng }
    : null;
  
  // Poll for task status when we have an active processing task
  useEffect(() => {
    let pollInterval;
    let progressInterval;
    
    // Define the polling function within this effect
    const pollTaskStatus = async () => {
      if (!processingTask || !processingTask.taskId) return;
      
      try {
        const taskStatus = await processingTask.pollTaskStatus();
        console.log('Task status poll result:', taskStatus);
        
        if (taskStatus.status === 'completed') {
          console.log('Task completed, fetching result...');
          clearInterval(pollInterval);
          clearInterval(progressInterval);
          
          try {
            const taskResult = await processingTask.getTaskResult();
            console.log('Task result:', taskResult);
            
            if (taskResult && taskResult.image_data) {
              setSatelliteImage(taskResult.image_data);
              setProcessingTask(null);
              setProcessingProgress(100);
              setProcessingMessage('Processing complete!');
            } else {
              setError('Task completed but returned invalid result format');
              setProcessingTask(null);
            }
          } catch (resultError) {
            console.error('Error fetching task result:', resultError);
            setError(`Error retrieving task result: ${resultError.message}`);
            setProcessingTask(null);
          }
        } else if (taskStatus.status === 'failed') {
          console.error('Task failed:', taskStatus.error || 'Unknown error');
          setError(`Background processing failed: ${taskStatus.error || 'Unknown error'}`);
          clearInterval(pollInterval);
          clearInterval(progressInterval);
          setProcessingTask(null);
        } else {
          // Still processing, update message
          setProcessingMessage(taskStatus.message || 'Processing NDVI visualization...');
        }
      } catch (pollError) {
        console.error('Error polling task status:', pollError);
        // Don't stop polling on a single error, just log it
      }
    };
    
    if (processingTask && processingTask.taskId) {
      // Start with immediate poll
      pollTaskStatus();
      
      // Then poll every 5 seconds
      pollInterval = setInterval(() => {
        pollTaskStatus();
      }, 5000);
      
      // Update progress animation
      progressInterval = setInterval(() => {
        setProcessingProgress(prev => {
          // Slowly increase progress up to 90%
          const newProgress = prev + 0.5;
          return newProgress < 90 ? newProgress : 90;
        });
      }, 1000);
      
      return () => {
        clearInterval(pollInterval);
        clearInterval(progressInterval);
      };
    }
    
    return () => {
      if (pollInterval) clearInterval(pollInterval);
      if (progressInterval) clearInterval(progressInterval);
    };
  }, [processingTask, farmId, visualizationType]);

  // Fetch satellite imagery for the current farm and visualization type
  const fetchSatelliteImagery = useCallback(async () => {
    if (!farmId) return;

    try {
      setIsLoading(true);
      setError(null);
      
      console.log(`Fetching satellite imagery for ${farmId} with visualization ${visualizationType}`);
      const response = await apiService.getFarmSatelliteImagery(farmId, visualizationType);
      
      // Check if this is a mock response from a timeout
      if (response && response.meta && response.meta.is_mock) {
        console.log('Received mock image data due to API timeouts');
        setSatelliteImage(response.image_data);
        setProcessingTask(null);
        // Show a warning to the user that this is a mock image
        setError(response.meta.message || 'Using cached/mock data due to API timeout');
      }
      // Check if we received a background processing task
      else if (response && response.isProcessing && response.taskId) {
        console.log('Background processing detected. Setting up polling...', response);
        
        // Store the processing task to track in the UI
        setProcessingTask(response);
        setProcessingProgress(5); // Start at 5%
        setProcessingMessage(response.message || 'Processing satellite imagery...');
        
        // Make debug info visible
        setShowDebug(true);
      } else if (response && response.image_data) {
        console.log('Image data received directly:', response.image_data.substring(0, 50) + '...');
        setSatelliteImage(response.image_data);
        setProcessingTask(null);
      } else {
        console.error('Unexpected response format:', response);
        setError('Received an invalid response format from the server');
      }
    } catch (error) {
      console.error('Error fetching satellite imagery:', error);
      
      // Special handling for timeout errors
      if (error.message && error.message.includes('timeout')) {
        if (visualizationType === 'ndvi') {
          setError('NDVI processing timed out. The API might be under heavy load. Retrying with mock data...');
          
          // We'll rely on the fallback/mock mechanism in api.js to handle this
          try {
            const mockResponse = await apiService.getFarmSatelliteImagery(farmId, visualizationType);
            if (mockResponse && mockResponse.image_data) {
              console.log('Successfully retrieved mock NDVI image after timeout');
              setSatelliteImage(mockResponse.image_data);
              setError('Using mock NDVI data due to API timeout. Refresh to try again with real data.');
            }
          } catch (retryError) {
            console.error('Even mock data retrieval failed:', retryError);
            setError('All attempts to retrieve NDVI data failed. Please try again later.');
          }
        } else {
          setError('Request timed out. The API might be under heavy load. Please try again later.');
        }
      } else {
        setError(`Error fetching satellite imagery: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsLoading(false);
    }
  }, [farmId, visualizationType]);
  
  // Initial fetch when component mounts or farmId/visualizationType changes
  useEffect(() => {
    fetchSatelliteImagery();
  }, [fetchSatelliteImagery, farmId, visualizationType]);
  if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
    return <div style={{color: 'red', padding: 20}}>Google Maps API key is missing. Please set REACT_APP_GOOGLE_MAPS_API_KEY in your .env file.</div>;
  }

  return (
    <Card className={classes.root}>
      <Typography variant="h6" className={classes.title}>
        <Satellite style={{ marginRight: "8px", verticalAlign: "middle" }} />
        Satellite View
      </Typography>
      {error && (
        <div className={classes.error}>{error}</div>
      )}
      
      {/* Debugging element */}
      {(showDebug || processingTask) && (
        <div style={{ 
          position: 'absolute', 
          top: '10px', 
          left: '10px', 
          padding: '10px', 
          backgroundColor: 'rgba(255, 255, 0, 0.8)', 
          zIndex: 9999, 
          fontSize: '12px',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}>
          Debug: {processingTask ? `Processing Task Active (${processingTask.taskId})` : 'Waiting for NDVI request'}
        </div>
      )}
      
      {/* Background Processing Overlay */}
      {processingTask && (
        <Paper className={classes.processingOverlay}>
          <Typography variant="h6">
            <Satellite style={{ marginRight: "8px", verticalAlign: "middle" }} />
            Processing NDVI
          </Typography>
          <LinearProgress 
            className={classes.progress} 
            variant="determinate" 
            value={processingProgress} 
          />
          <Typography variant="body2" className={classes.processingMessage}>
            {processingMessage || 'Please wait while we process your request...'}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            The analysis will be cached for faster access in the future
          </Typography>
          <Typography variant="caption" display="block" style={{ marginTop: '8px' }}>
            Task ID: {processingTask?.taskId || 'Unknown'}
          </Typography>
          {processingProgress === 100 && (
            <Button 
              variant="contained" 
              color="primary" 
              size="small" 
              style={{ marginTop: '16px' }}
              onClick={() => setProcessingMessage('')}
            >
              <Refresh fontSize="small" style={{ marginRight: '4px' }} />
              Dismiss
            </Button>
          )}
        </Paper>
      )}
      <div className={classes.visualizationControls}>
        <ButtonGroup size="small" color="primary" aria-label="visualization type">
          <Button 
            variant={visualizationType === 'true-color' ? 'contained' : 'outlined'}
            onClick={() => setVisualizationType('true-color')}
          >
            True Color
          </Button>
          <Button 
            variant={visualizationType === 'false-color' ? 'contained' : 'outlined'}
            onClick={() => setVisualizationType('false-color')}
          >
            False Color
          </Button>
          <Button 
            variant={visualizationType === 'ndvi' ? 'contained' : 'outlined'}
            onClick={() => setVisualizationType('ndvi')}
          >
            NDVI
          </Button>
        </ButtonGroup>
      </div>
      <div className={classes.mapContainer}>
        <MapComponent
          googleMapURL={`https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry,drawing,places&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`}
          loadingElement={<div style={{ height: "100%" }} />}
          containerElement={<div style={{ height: "100%" }} />}
          mapElement={<div style={{ height: "100%" }} />}
          center={center}
          satelliteImage={satelliteImage}
          isLoading={isLoading}
          visualizationType={visualizationType}
        />
      </div>
      <div className={classes.controlPanel}>
        <div className={classes.controlButton}>
          <ZoomIn />
        </div>
        <div className={classes.controlButton}>
          <ZoomOut />
        </div>
        <div className={classes.controlButton}>
          <MyLocation />
        </div>
        <div className={classes.controlButton}>
          <Layers />
        </div>
      </div>
    </Card>
  );
}
